import React, { useEffect, useState } from 'react';
import { View, Pressable } from 'react-native';
import { useQuery, useLazyQuery } from '@apollo/client';
import { PencilSimple, Prohibit } from '@phosphor-icons/react';

import { SUGGEST_THINGS } from 'app/src/components/Things/queries';
import { ICON_SIZES } from 'app/src/elements/Icon';
import colors from 'app/src/styles/colors';
import { Button } from 'app/src/elements/buttons';
import {
  Container,
  ButtonContainer,
  EmojiText,
  LabelText,
} from 'app/src/elements/ButtonPicker';
import { StyledTextInput, FieldLabel } from 'app/src/elements/inputs';
import Modal from 'app/src/elements/Modal';
import { Text, Subtitle } from 'app/src/styles';
import { medicineDose } from 'app/src/utils/formatting';

export const EventThingActions = props => {
  const { thing, eventThing, setValues, noNotes } = props;

  const [showEdit, setShowEdit] = useState(false);

  return (
    <>
      {!noNotes && (
        <Pressable
          onPress={() => setShowEdit(true)}
          accessibilityLabel="Edit notes"
        >
          <PencilSimple
            size={ICON_SIZES.button}
            weight="regular"
            color={eventThing.notes ? colors.success : colors.textMuted}
          />
        </Pressable>
      )}

      <Pressable
        onPress={() => setValues(thing, { selected: false })}
        accessibilityLabel="Remove selection"
      >
        <Prohibit
          size={ICON_SIZES.button}
          weight="regular"
          color={colors.textMuted}
        />
      </Pressable>

      {showEdit && (
        <Modal title={`${thing.name}`} setShow={setShowEdit}>
          <FieldLabel name="notes" label="Notes" />
          <StyledTextInput
            value={eventThing.notes}
            onChangeText={value => setValues(thing, { notes: value })}
          />
          <Button label="Close" onPress={() => setShowEdit(false)} wide />
        </Modal>
      )}
    </>
  );
};

export const EventThingButton = props => {
  const { thing, eventThing, setValues } = props;

  return (
    <ButtonContainer
      onPress={() => (
        eventThing.selected ? null : setValues(thing, { selected: true })
      )}
      notSelected={!eventThing.selected}
    >
      <EmojiText>
        {thing.emoji || thing.name[0]}
      </EmojiText>
      <LabelText>
        {thing.name}
      </LabelText>
      {!!medicineDose(thing) && (
        <LabelText style={{ marginTop: 0 }}>
          {medicineDose(thing)}
        </LabelText>
      )}

      {eventThing.selected && (
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'space-around',
            width: '100%'
          }}
        >
          <EventThingActions {...props} />
        </View>
      )}
    </ButtonContainer>
  );
};

export const EventThingListItem = props => {
  const { thing, eventThing } = props;

  return (
    <View style={{ flexDirection: 'row', gap: 20 }}>
      <Text>
        {thing?.name}
        {!!medicineDose(thing) && ` ${medicineDose(thing)}`}
      </Text>

      {!!eventThing.notes && (
        <Text style={{ fontStyle: 'italic' }}>{eventThing.notes}</Text>
      )}

      <EventThingActions {...props} />
    </View>
  );
};

export const MedicineGroupButton = ({ group, selectedGroupId, onGroupSelect }) => {
  const isSelected = selectedGroupId === group.id;

  return (
    <ButtonContainer
      onPress={() => onGroupSelect(group)}
      notSelected={!isSelected}
    >
      <EmojiText>💊</EmojiText>
      <LabelText>{group.name}</LabelText>
      {group.scheduledTime && (
        <LabelText>{group.scheduledTime}</LabelText>
      )}
    </ButtonContainer>
  );
};

const EventThings = ({ eventThings, setEventThings, watch, ...props }) => {
  const clientId = watch('clientId') || null;
  const eventTypeId = watch('eventTypeId') || null;

  const [showButtons, setShowButtons] = useState(props.showButtons);
  const [showAllButtons, setShowAllButtons] = useState(
    props.showAllButtons || false
  );

  const [things, setThings] = useState();

  const { data } = useQuery(props.query || SUGGEST_THINGS, {
    variables: { clientId, eventTypeId, all: showAllButtons }
  });

  useEffect(() => {
    if (data) { setThings(data.things || data.medicines || data.providers); }
  }, [data]);

  if (!things) { return null; }

  const eventThing = thing => (eventThings[thing.id] || { thingId: thing.id });

  const setValues = (thing, values) => {
    const eventThingData = { ...eventThing(thing), ...values };
    setEventThings({ ...eventThings, [thing.id]: eventThingData });
  };

  const selectedEventThings = Object.keys(eventThings)
    .map(thingId => eventThings[thingId])
    .filter(eventThing => eventThing?.selected);

  const thingsWithAdded = [...things, ...(props.addedThings || [])];

  return (
    <View>
      <Subtitle>{props.title || 'Things'}</Subtitle>

      {props.groupNote && (
        <Text style={{ fontStyle: 'italic', marginBottom: 4 }}>
          {props.groupNote}
        </Text>
      )}

      {(!showButtons || things.length > 0) && (
        <Pressable onPress={() => setShowButtons(!showButtons)}>
          <Text>[{showButtons ? 'Hide' : 'Show'} Selection Buttons]</Text>
        </Pressable>
      )}

      {showButtons && !showAllButtons && (
        <Pressable onPress={() => setShowAllButtons(true)}>
          <Text>[Show All Buttons]</Text>
        </Pressable>
      )}

      {showButtons && props.groups && props.groups.length > 0 && (
        <>
          <Text style={{ marginTop: 8, marginBottom: 4 }}>Groups</Text>
          <Container>
            {props.groups.map(group => (
              <MedicineGroupButton
                key={group.id}
                group={group}
                selectedGroupId={props.selectedGroupId}
                onGroupSelect={props.onGroupSelect}
              />
            ))}
          </Container>
        </>
      )}

      {showButtons && (
        <Container>
          {thingsWithAdded.map(thing => (
            <EventThingButton
              key={thing.id}
              thing={thing}
              eventThing={eventThing(thing)}
              setValues={setValues}
              noNotes={props.noNotes}
            />
          ))}

          {props.add && (
            <ButtonContainer onPress={props.add} notSelected>
              <Text style={{ fontSize: 40 }}>+</Text>
              <Text style={{ fontSize: 12 }}>Add</Text>
            </ButtonContainer>
          )}
        </Container>
      )}

      {selectedEventThings.map(eventThing => (
        <EventThingListItem
          key={eventThing.thingId}
          thing={thingsWithAdded.find(thing => thing.id === eventThing.thingId)}
          eventThing={eventThing}
          setValues={setValues}
          noNotes={props.noNotes}
        />
      ))}
    </View>
  );
};

export default EventThings;
